/**
 * Retour de Stripe Checkout après un paiement de billet.
 *
 * Le paiement lui-même est confirmé de façon asynchrone par le webhook
 * (checkout.session.completed) — cet écran ne fait qu'attendre poliment que
 * la commande passe « payee » puis renvoie vers l'e-mail comme source de
 * vérité (billets + QR), plutôt que de reconstruire l'affichage ici.
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Marque } from '../composants/Marque.tsx';

type EtatCommande = { statut: string; evenement_nom: string; nb_billets: number };

export function BilletterieConfirmation() {
  const [params] = useSearchParams();
  const commandeId = params.get('commande');
  const [commande, setCommande] = useState<EtatCommande | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const tentatives = useRef(0);

  useEffect(() => {
    if (!commandeId) {
      setErreur('Commande introuvable');
      return;
    }
    let annule = false;

    const verifier = async () => {
      try {
        const r = await fetch(`/api/billetterie/commandes/${commandeId}`);
        if (!r.ok) throw new Error();
        const d = (await r.json()) as EtatCommande;
        if (annule) return;
        setCommande(d);
        // Le webhook Stripe arrive en général en quelques secondes — on
        // patiente un peu avant d'afficher un état encore « en_attente ».
        if (d.statut === 'en_attente' && tentatives.current < 8) {
          tentatives.current += 1;
          setTimeout(verifier, 2000);
        }
      } catch {
        if (!annule) setErreur('Connexion indisponible');
      }
    };
    void verifier();
    return () => {
      annule = true;
    };
  }, [commandeId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-void px-4 font-sans text-blanc">
      <div className="w-full max-w-[480px] text-center">
        <div className="mb-8 flex items-center justify-center gap-2.5">
          <Marque />
          <span className="text-[15px] font-medium text-blanc">
            EventMatch<span className="text-fumee">.pro</span>
          </span>
        </div>

        {erreur && <p className="text-[15px] text-fumee">{erreur}</p>}

        {!erreur && !commande && <p className="text-[15px] text-fumee">Vérification du paiement…</p>}

        {commande?.statut === 'payee' && (
          <>
            <h1 className="display-title text-[28px] text-blanc">
              Paiement <em className="text-match-gradient font-serif italic font-medium">confirmé</em>
            </h1>
            <p className="mt-3 text-[15px] leading-relaxed text-fumee">
              {commande.nb_billets} billet(s) pour {commande.evenement_nom}. Chaque porteur reçoit son billet et son
              QR d'entrée par e-mail à l'instant.
            </p>
          </>
        )}

        {commande?.statut === 'en_attente' && (
          <p className="text-[15px] text-fumee">
            Paiement reçu par Stripe, confirmation en cours — actualisez cette page dans quelques instants si rien
            n'apparaît.
          </p>
        )}

        {(commande?.statut === 'expiree' || commande?.statut === 'annulee') && (
          <p className="text-[15px] text-fumee">
            Cette commande n'a pas abouti — retournez à la page de l'événement pour réessayer.
          </p>
        )}
      </div>
    </div>
  );
}
